import React, { useEffect } from 'react';
import { useBasket } from '~/features/basket';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import { StyledCheckoutContainer, StyledLoader, StyledSpinner } from './../styled';
import { DeliverInformation } from '~/features/checkout/components/DeliveryInformation/DeliveryInformation';
import { CustomerInformation } from '~/features/checkout/components/CustomerInformation';
import { useRouter } from 'next/router';
import { useFrame } from '~/shared/utils';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { InStore } from '~/features/checkout/components/InStore';
import { InStoreFinishOrder } from '~/features/checkout/components/InStoreFinishOrder/InStoreFinishOrder';
import { useEvents } from '~/shared/hooks/useEvents';
import { useTranslation } from '~/shared/utils/translation';
import { Text } from '~/shared/components';
import Payment from '~/features/checkout/components/Payment/Payment';
import { resetAltaPaySession } from '~/services/altapay/hooks';
import { LockedFeatureKey, useLockedFeature } from '~/shared/hooks/useLockedFeature';

export const Checkout = () => {
    const { data: frame } = useFrame();
    const { translate } = useTranslation();

    const {
        currentStep,
        setOnBackToStart,
        setDeliveryMethod,
        steps: pagesCheckout,
        setSteps: setPagesCheckout,
        setOrderNumber,
        setCurrentStep,
    } = useCheckoutState();

    const basket = useBasket();
    const { isStorePersonnel } = useCustomer();
    const { identifyBasketIdEvent } = useEvents(frame);
    const { push } = useRouter();

    const currentPage = pagesCheckout[currentStep];

    /**
     * Listen for query parameters to toggle locked feature during checkout.
     *
     * TODO(IMC-2285): Remove implementation once core issue has been resolved.
     */
    useLockedFeature(LockedFeatureKey.APPLE_PAY);

    /**
     * Store personnels will have a different set of steps
     * compared to regular customers.
     */
    useEffect(() => {
        if (isStorePersonnel) {
            setDeliveryMethod('clickAndCollect');

            setPagesCheckout([
                { component: <InStore />, title: translate('checkout.checkoutStep1') },
                { component: <DeliverInformation />, title: translate('checkout.checkoutStep2') },
                { component: <InStoreFinishOrder />, title: translate('checkout.checkoutStep3') },
                { title: translate('checkout.checkoutStep4') },
            ]);
        } else {
            setPagesCheckout([
                {
                    component: <CustomerInformation />,
                    title: translate('checkout.checkoutStep1'),
                },
                {
                    component: <DeliverInformation />,
                    title: translate('checkout.checkoutStep2'),
                },
                {
                    component: <Payment />,
                    title: translate('checkout.checkoutStep3'),
                },
                {
                    title: translate('checkout.checkoutStep4'),
                },
            ]);
        }
    }, [isStorePersonnel]);

    useEffect(() => {
        if (basket.basketId) identifyBasketIdEvent(basket.basketId);
    }, [basket.basketId]);

    useEffect(() => {
        setOnBackToStart(() => {
            push(frame.settings?.pageReferences.basketPage?.url ?? '/');
        });

        // Reset to start of flow.
        setCurrentStep(0);

        // Ensure that we have a fresh order number and AltaPay session on checkount start.
        resetAltaPaySession();
        setOrderNumber(undefined);

        // Reset to start of flow.
        setCurrentStep(0);
    }, []);

    useEffect(() => {
        /**
         * The basket is loaded asynchronously client-side. We'll wait for it to be loaded before
         * doing any evaluation/decisions.
         */
        if (!basket.loaded) return;

        if (basket.itemCount() === 0) {
            // We'll go to the basket page if we don't have any products added to the cart.
            push(frame.settings?.pageReferences.basketPage?.url ?? '/');
        }
    }, [basket.loaded]);

    useEffect(() => {
        if (!pagesCheckout[currentStep]?.title) {
            return;
        }

        const checkoutUrl = `${
            frame.settings?.pageReferences.checkoutPage?.url
        }?step=${pagesCheckout[currentStep].title.toLowerCase()}`;

        push(checkoutUrl, checkoutUrl, {
            shallow: true,
        });
    }, [currentStep, pagesCheckout]);

    return (
        <StyledCheckoutContainer>
            {basket.loaded ? (
                currentPage?.component
            ) : (
                <StyledLoader alignItems="center" justifyContent="center" column>
                    <Text variant="display3">{translate('checkout.loadingTitle')}</Text>
                    <StyledSpinner />
                    <Text variant="bodySmall">{translate('checkout.loadingBodyText')}</Text>
                </StyledLoader>
            )}
        </StyledCheckoutContainer>
    );
};
